import { formatCurrencyCompact } from '../parity-dev/cfo/ceo/ceoFormat'
import type { CeoCockpitResponse } from '../parity-dev/cfo/ceo/ceoCockpitTypes'

/**
 * The governed fact map behind `{{fact:*}}` tokens.
 *
 * Every value is a PRE-FORMATTED display string derived from the same CEO
 * cockpit contract the tiles render — no model call, no re-derivation, so the
 * narration can never quote a number that isn't on screen. Facts that the
 * contract can't supply are simply absent: the token resolver then falls back
 * to the token's declared default, which the schema requires — a prospect
 * never sees literal braces.
 *
 * Sound stays generic by design: narration audio is pre-generated from
 * defaults, so fact tokens belong in BODY copy (live text), not voice scripts.
 */
export function buildTourFacts(cockpit: CeoCockpitResponse | null | undefined): Record<string, string> {
  if (!cockpit) return {}
  const facts: Record<string, string> = {}
  const put = (key: string, value: string | null) => {
    if (value != null && value.trim()) facts[key] = value.trim()
  }
  const money = (v: number | null | undefined) =>
    typeof v === 'number' && Number.isFinite(v) ? formatCurrencyCompact(v) : null

  const cash = cockpit.cash
  put('cash_on_hand', money(cash?.cash_estimated_as_of ?? cash?.cash_at_close))
  put('ar_open', money(cash?.ar_open ?? cockpit.customers?.ar_total))
  const dso = cash?.dso ?? cockpit.customers?.dso
  put('dso_days', typeof dso === 'number' && Number.isFinite(dso) ? `${Math.round(dso)} days` : null)
  const months = cash?.months_of_cash ?? cash?.runway_months
  put('months_of_cash', typeof months === 'number' && Number.isFinite(months) ? `${months.toFixed(1)} months` : null)
  put('monthly_outflow', money(cash?.monthly_outflow_avg_3m))

  const top1 = cockpit.customers?.concentration?.top1_share
  put('top_customer_share', typeof top1 === 'number' && Number.isFinite(top1) ? `${Math.round(top1 * 100)}%` : null)

  put('company_name', cockpit.company?.name ?? null)
  return facts
}

/** Inputs the sample-company side of `their_numbers` reads from the fact map. */
export const THEIR_NUMBERS_FACT_KEYS = ['company_name', 'ar_open', 'dso_days'] as const
